/**
 * Rueckgaengig-Stapel (F-45) - nur im Speicher, nicht gespeichert.
 *
 * Jede aufgezeichnete Aktion (Loeschen, Abhaken, Umwandeln ...) kommt als
 * Meldung an (`db.aufzeichnung.beiAktion`, eingehaengt im RueckgaengigHost),
 * landet oben auf dem Stapel und wird als Meldung mit „Rückgängig“ gezeigt.
 * Strg+Z nimmt die oberste zurueck. Eigene Datei wegen der react-refresh-Regel.
 */
import { create } from 'zustand';

import { wiederherstellen, zuruecknehmen } from '@/db/repositories/papierkorb';
import type { AktionsMeldung } from '@/domain/papierkorb/typen';
import { meldungText, wiederherstellText, zuruecknehmenText } from '@/modules/papierkorb/texte';

/** So viele Aktionen lassen sich nacheinander zuruecknehmen. */
export const STAPEL_MAX = 20;

/** Strg+Z bzw. Cmd+Z - mit e.key statt e.code: Auf QWERTZ liegt das Z dort, wo QWERTY das Y hat. */
export const istRueckgaengigTaste = (e: KeyboardEvent): boolean =>
  (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && !e.repeat && !e.isComposing && e.key.toLowerCase() === 'z';

export interface UndoAktion extends AktionsMeldung {
  nr: number;
}

/** Was die Meldung gerade zeigt - eine Aktion mit Knopf oder nur eine Rueckmeldung. */
export interface Anzeige {
  nr: number;
  text: string;
  aktion?: UndoAktion;
  fehler?: boolean;
}

interface RueckgaengigState {
  stapel: UndoAktion[];
  anzeige: Anzeige | null;
  /** Laeuft gerade eine Ruecknahme? Sperrt Doppelklick und Tastenwiederholung. */
  laeuft: boolean;
  merken: (m: AktionsMeldung) => void;
  /** Die oberste (oder die genannte) Aktion zuruecknehmen. `false`: nichts zu tun. */
  rueckgaengigMachen: (a?: UndoAktion) => Promise<boolean>;
  /** Ein Papierkorb-Eintrag ist anderweitig erledigt (Seite: wiederhergestellt, endgueltig geloescht). */
  vergessen: (eintragId: string) => void;
  /** Alles vergessen - nach dem Einspielen einer Sicherung gehoert nichts mehr zum Stand. */
  allesVergessen: () => void;
  schliessen: () => void;
}

let zaehler = 0;

export const useRueckgaengig = create<RueckgaengigState>()((set, get) => ({
  stapel: [],
  anzeige: null,
  laeuft: false,

  merken: (m) => {
    const aktion: UndoAktion = { ...m, nr: ++zaehler };
    set((s) => ({
      stapel: [...s.stapel, aktion].slice(-STAPEL_MAX),
      anzeige: { nr: aktion.nr, text: meldungText(m), aktion },
    }));
  },

  rueckgaengigMachen: async (gewaehlt) => {
    const { stapel, laeuft } = get();
    const a = gewaehlt ?? stapel.at(-1);
    if (!a || laeuft) return false;
    set((s) => ({ laeuft: true, stapel: s.stapel.filter((x) => x.nr !== a.nr) }));
    let rueck: { text: string; fehler: boolean };
    try {
      rueck = a.eintragId
        ? wiederherstellText(await wiederherstellen(a.eintragId), a.titel)
        : zuruecknehmenText(await zuruecknehmen(a.aenderungen ?? []), a.titel);
    } catch (err) {
      console.error('Rückgängig:', err);
      rueck = wiederherstellText(null, a.titel);
    }
    set({ laeuft: false, anzeige: { nr: ++zaehler, ...rueck } });
    return true;
  },

  vergessen: (eintragId) => set((s) => ({
    stapel: s.stapel.filter((x) => x.eintragId !== eintragId),
    anzeige: s.anzeige?.aktion?.eintragId === eintragId ? null : s.anzeige,
  })),

  allesVergessen: () => set({ stapel: [], anzeige: null }),

  schliessen: () => set({ anzeige: null }),
}));
